import type React from 'react';
import { Edit2 } from 'react-feather';
import styled from '@emotion/styled';
import { FormButtons } from './FormButtons';

export type EditActionButtonsProps = {
  isEditing: boolean;
  isSaving?: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  /** White-label primary color for the idle Edit button (e.g. frontend's AppThemingContext). */
  themedColor?: string;
  /** Diameter of the idle Edit button, and the basis for its icon size. Defaults to the size
   * originally used on the Tag Details screen, where this component was first built. */
  size?: number;
};

/**
 * Reusable circular edit / save / cancel icon buttons for a section header. Originated on the
 * Tag Details screen (Custom Summary Prompt / Additional Context sections) and promoted here so
 * every "click a pencil to edit this field" affordance in the app — Segment Profile, Voice
 * Settings, Tag Details — renders identically instead of each owning its own button styling.
 */
export const EditActionButtons = ({
  isEditing,
  isSaving = false,
  onEdit,
  onSave,
  onCancel,
  themedColor,
  size = 28,
}: EditActionButtonsProps) =>
  isEditing ? (
    <FormButtons disabled={isSaving} onCancel={onCancel} onSubmit={onSave} size={size / 2} />
  ) : (
    <RoundButton
      $size={size}
      onClick={onEdit}
      title="Edit"
      type="button"
      style={themedColor ? ({ '--themed-color': themedColor } as React.CSSProperties) : undefined}
    >
      <Edit2 size={size / 2} />
    </RoundButton>
  );

EditActionButtons.displayName = 'EditActionButtons';

const RoundButton = styled.button<{ $size: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  box-sizing: border-box;
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid var(--themed-color, var(--pri-02));
  color: var(--themed-color, var(--pri-02));
  background: var(--white);
  transition: all 0.2s ease;

  &:hover {
    color: white;
    background: var(--themed-color, var(--pri-02));
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
